import React from "react";

import { fetchDispatchGeotabCheck } from "../api/geotabApi.js";
import { getLocalTodayDispatchDate } from "../utils/dispatchDayCalendarUtils.js";
import { useDispatchGeotabLimits } from "./useDispatchGeotabReal.js";

// Écarts Geotab : 2 s après l'affichage du Dispatch, quand le navigateur est libre ; changer de date annule la lecture.
// Une lecture par version du plan : gardée 30 min pour le passé, actualisée aux 2 min aujourd'hui comme le Réel.
const START_DELAY_MS = 2000;
const CACHE_MS = 30 * 60 * 1000;
const TODAY_REFRESH_MS = 2 * 60 * 1000;
const loads = new Map(); // clé → { at, promise }
// Dernier état de chaque journée, lu aussi par la fenêtre « Clore la journée » :
// { key, status: "waiting" | "loading" | "ready" | "error", data, start } ; start lance tout de suite une lecture en attente.
const byDate = new Map();
const listeners = new Set();

function setEntry(date, entry) {
  byDate.set(date, entry);
  listeners.forEach((listener) => listener());
}

function load(key, date, revision, limits, maxAgeMs = CACHE_MS) {
  const hit = loads.get(key);
  if (hit && Date.now() - hit.at < maxAgeMs) return hit.promise;
  // Une seule version gardée par journée.
  [...loads.keys()].filter((other) => other.startsWith(`${date}:`)).forEach((other) => loads.delete(other));
  const promise = fetchDispatchGeotabCheck(date, limits, revision);
  loads.set(key, { at: Date.now(), promise });
  promise.catch(() => loads.delete(key));
  return promise;
}

export function subscribeDispatchGeotabCheck(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const peekDispatchGeotabCheck = (date) => byDate.get(date) || null;

// Position confirmée ou retirée : la liste de la journée est relue tout de suite (le serveur a oublié sa lecture gardée).
export function reloadDispatchGeotabCheck(date) {
  const entry = byDate.get(date);
  if (!entry?.key || !entry.query) return Promise.resolve();
  loads.delete(entry.key);
  return load(entry.key, date, entry.query.revision, entry.query.limits)
    .then((data) => { if (byDate.get(date)?.key === entry.key) setEntry(date, { ...byDate.get(date), status: "ready", data }); });
}

// Écarts du lecteur : arrêts hors du Dispatch, puis camions dont seuls des sites prévus n'ont pas été vus (case « missed »).
export function gapItemsOf(data) {
  if (!data?.eligible) return [];
  const items = data.items || [];
  const flagged = new Set(items.map((item) => item.truck_id));
  return [...items, ...(data.missed || []).filter((m) => !flagged.has(m.truck_id)).map((m) => ({ ...m, case: "missed", stops: [] }))];
}

// Fenêtre Geotab ouverte ({ date, index, tab, closing }) et camions déjà regardés : partagés par l'épingle du haut,
// « Clore la journée » et le compteur Geotab des lignes du Gantt. tab : « orders » ouvre la liste de la journée, « trucks »
// le lecteur de l'écart index ; closing ({ title, confirm, cancel }) : la fenêtre tient lieu de « Clore la journée ».
let gapView = null;
const seen = new Set();
export const peekDispatchGeotabGap = () => gapView;
export function openDispatchGeotabGap(date, index, tab = "trucks", closing = null) {
  gapView = index == null ? null : { date, index, tab, closing };
  const truckId = gapView && tab !== "orders" && gapItemsOf(byDate.get(date)?.data)[index]?.truck_id;
  if (truckId) seen.add(`${date}:${truckId}`);
  listeners.forEach((listener) => listener());
}

// Écarts par camion d'une journée : { count (arrêts hors du Dispatch + sites prévus pas vus), items, seen, open }.
export function useDispatchGeotabGaps(date) {
  const entry = React.useSyncExternalStore(subscribeDispatchGeotabCheck, () => byDate.get(date), () => undefined);
  const view = React.useSyncExternalStore(subscribeDispatchGeotabCheck, peekDispatchGeotabGap, () => null);
  return React.useMemo(() => {
    const byTruck = new Map();
    const missed = new Map((entry?.data?.missed || []).map((m) => [m.truck_id, m.sites]));
    gapItemsOf(entry?.data).forEach((item, index) => {
      const gap = byTruck.get(item.truck_id)
        || { count: missed.get(item.truck_id)?.length || 0, missed: missed.get(item.truck_id) || [], items: [], seen: seen.has(`${date}:${item.truck_id}`), open: () => openDispatchGeotabGap(date, index) };
      gap.count += (item.stops || []).length;
      if (item.stops?.length) gap.items.push(item);
      byTruck.set(item.truck_id, gap);
    });
    return byTruck;
  }, [entry, view, date]); // view : « déjà vu » change à chaque ouverture
}

// paused : le Dispatch recharge (changement de date, mise à jour) ; la lecture attend qu'il ait fini, puis repart de zéro.
export default function useDispatchGeotabCheck({ date = "", revision = "", enabled = false, paused = false }) {
  const limits = useDispatchGeotabLimits();
  const today = getLocalTodayDispatchDate();
  const key = enabled && limits && date && date <= today ? `${date}:${revision}:${JSON.stringify(limits)}` : "";
  const entry = React.useSyncExternalStore(subscribeDispatchGeotabCheck, () => (date ? byDate.get(date) : undefined), () => undefined);
  React.useEffect(() => {
    if (!key || paused) return undefined;
    let cancelled = false;
    let timer = null;
    let idle = null;
    const isToday = date === today;
    const maxAgeMs = isToday ? TODAY_REFRESH_MS : CACHE_MS;
    const run = (cacheAgeMs = maxAgeMs) => {
      if (cancelled) return;
      window.clearTimeout(timer);
      if (idle != null) window.cancelIdleCallback?.(idle);
      setEntry(date, { ...byDate.get(date), key, status: "loading", start: null });
      load(key, date, revision, limits, cacheAgeMs).then(
        // query : seuils et version lus, repris par le tracé GPS de « Voir » pour partager la même lecture de Geotab.
        (data) => { if (!cancelled) setEntry(date, { key, status: "ready", data, start: null, query: { limits, revision } }); },
        () => { if (!cancelled) setEntry(date, { key, status: "error", data: null, start: null }); },
      );
    };
    const refresh = isToday ? window.setInterval(() => { if (document.visibilityState === "visible") run(0); }, TODAY_REFRESH_MS) : null;
    const hit = loads.get(key);
    if (hit && Date.now() - hit.at < maxAgeMs) {
      run();
      return () => { cancelled = true; window.clearInterval(refresh); };
    }
    // Pendant l'attente, l'ancien résultat de la journée reste affiché : l'icône ne s'éteint pas à chaque modification.
    const previous = byDate.get(date);
    setEntry(date, { data: previous?.status === "ready" ? previous.data : null, query: previous?.query, key, status: "waiting", start: run });
    timer = window.setTimeout(() => {
      idle = window.requestIdleCallback?.(() => run(), { timeout: 3000 }) ?? null;
      if (idle == null) run();
    }, START_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.clearInterval(refresh);
      if (idle != null) window.cancelIdleCallback?.(idle);
    };
  }, [key, date, revision, limits, paused, today]);
  return key && entry?.key ? entry : null;
}
